import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Layers,
  Plus,
  Edit,
  Trash2,
  MoreHorizontal,
  DollarSign,
} from "lucide-react";
import { ProductVariant } from "@/types/product-type";
import { productVariantService } from "@/services/product-variant-service";
import { VariantStockInfo } from "./VariantStockInfo";

interface VariantCardProps {
  variant: ProductVariant;
  onEdit: (variant: ProductVariant) => void;
  onDelete: (variant: ProductVariant) => void;
  onAddStock: (variantId: string) => void;
}

export function VariantCard({
  variant,
  onEdit,
  onDelete,
  onAddStock,
}: VariantCardProps) {
  const [imageError, setImageError] = useState(false);

  const formattedVariant = productVariantService.formatVariant({
    ...variant,
    price: variant.price ?? 0,
    cost: variant.cost ?? 0,
    storeId: 1,
    productName: variant.productName || "",
  });

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex gap-4">
          {/* Product Image */}
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
              {variant.imageUrl && !imageError ? (
                <img
                  src={variant.imageUrl}
                  alt={variant.name}
                  className="w-full h-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <Layers className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-3">
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-lg truncate">
                  {variant.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  SKU: {variant.sku}
                </p>

                {/* Pricing */}
                <div className="flex items-center gap-4 mt-1">
                  <div className="flex items-center gap-1">
                    <DollarSign className="h-3 w-3 text-green-600" />
                    <span className="text-sm font-medium text-green-600">
                      {formattedVariant.displayPrice}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Cost: {formattedVariant.displayCost}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 ml-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onAddStock(variant.id.toString())}
                  className="whitespace-nowrap"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Stock
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(variant)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Variant</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete the variant "
                            {variant.name}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDelete(variant)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Stock Information */}
            <VariantStockInfo stock={variant.stock} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
